"use strict"

import { AssignmentFormData } from "./types"
import AuthService, {
  UserTokenInfo,
} from "../../app/services/auth/auth.service"
import BreadcrumbService from "../../app/services/breadcrumb-service/breadcrumb.service"
import { HttpService } from "../../app/services/http/http.service"
import AssignmentService from "../../app/services/assignment/assignment.service"
import CacheService from "../../app/services/cache/cache.service"
import AssignmentTrackingService from "../../app/services/assignment-tracking/assignment-tracking.service"
import FirebaseService from "../../app/services/firebase/firebase.service"

import { AssignmentTags } from "../../app/model/domain/assignment"
import ElementMetadata, {
  ElementIntents,
} from "../../app/model/domain/element-metadata"
import { UserRoles } from "../../app/model/domain/user"
import Textbox from "../../app/model/ui/elements/textbox"
import Point from "../../app/model/ui/point"
import SlideBackground from "../../app/model/ui/elements/slide-background"
import FillInTheBlankParent, {
  FillInTheBlankAnswer,
} from "../../app/model/ui/elements/fill-in-the-blank-parent"
import { FitbAnswerTypes } from "../../app/components/assignment-toolbar/assignment-toolbar.directive"

// Background URL options
const BACKGROUND_URLS = [
  "https://storage.googleapis.com/slide-backgrounds/classkick_bagrounds_default_bg_pattern-2.png",
  "https://storage.googleapis.com/slide-backgrounds/classkick_bagrounds_default_bg_pattern-5.png",
  "https://storage.googleapis.com/slide-backgrounds/classkick_bagrounds_default_bg_pattern-6.png",
  "https://storage.googleapis.com/slide-backgrounds/classkick_bagrounds_default_bg_pattern-7.png",
]

interface AiChatResponse {
  choices: Array<{
    message: {
      content: string
    }
  }>
}

interface ParsedQuestion {
  text: string
  suggestedAnswers?: string[]
}

interface Question {
  id: string
}

interface Assignment {
  id: string
  name: string
  questions: Question[]
}

export class AssignmentWizardController {
  private user: UserTokenInfo
  private assignmentId?: string

  constructor(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    private environment: any, // TODO: migrate to typescript
    private AuthService: AuthService,
    private AssignmentService: AssignmentService,
    private CacheService: CacheService,
    private AssignmentTrackingService: AssignmentTrackingService,
    private FirebaseService: FirebaseService,
    private BreadcrumbService: BreadcrumbService,
    private HttpService: HttpService,
  ) {
    "ngInject"

    this.user = this.AuthService.authData

    // Bind methods for React components
    this.createAssignment = this.createAssignment.bind(this)
    this.closeWizard = this.closeWizard.bind(this)
    this.viewAssignment = this.viewAssignment.bind(this)

    if (!this.user?.isTeacher) {
      this.BreadcrumbService.go("root.account.home")
    }
  }

  async createAssignment(data: AssignmentFormData): Promise<void> {
    const { questions, assignmentName } =
      await this.generateQuestionsAndName(data)
    const assignment = await this.createAssignmentWithQuestions(
      data,
      questions,
      assignmentName,
    )
    await this.addElementsToQuestions(assignment, questions)

    this.assignmentId = assignment.id
  }

  private async generateQuestionsAndName(
    data: AssignmentFormData,
  ): Promise<{ questions: ParsedQuestion[]; assignmentName: string }> {
    const prompt = this.buildPrompt(data)
    const response = await this.callAICompletionAssistant(prompt)
    const { questions, assignmentName } = this.parseResponse(response, data)
    return { questions, assignmentName }
  }

  private async createAssignmentWithQuestions(
    data: AssignmentFormData,
    questions: ParsedQuestion[],
    assignmentName: string,
  ): Promise<Assignment> {
    // assignment_tag value field is varchar(190), choose 100 char to be safe
    const assignmentTags = [
      {
        name: AssignmentTags.AI_GENERATED,
        data: {
          created_by_ai: true,
          practice_type: this.truncateValue(data.practiceType, 100),
          grade_level: this.truncateValue(data.grade, 100),
          topic: this.truncateValue(data.practiceTopic, 100),
        },
      },
    ]

    const newAssignment = await this.AssignmentService.create(
      {
        name: assignmentName,
        tags: assignmentTags,
      },
      questions.length,
    )
    const assignment =
      await this.CacheService.addAssignmentForUser(newAssignment)

    this.AssignmentTrackingService.target = assignment
    return assignment
  }

  private async addElementsToQuestions(
    assignment: Assignment,
    questions: ParsedQuestion[],
  ): Promise<void> {
    const promises = assignment.questions.map(
      (question: Question, index: number) => {
        return this.addElementsToQuestion(question, questions[index], index)
      },
    )

    await Promise.all(promises)
  }

  private async addElementsToQuestion(
    question: Question,
    questionData: ParsedQuestion,
    questionIndex: number,
  ): Promise<void> {
    if (!questionData) return

    const metadata = this.createElementMetadata()

    await this.addSlideBackground(questionIndex, metadata)

    await this.addTextbox(questionData.text, questionIndex, metadata)

    // Add fitb only if we have suggested answers
    if (questionData.suggestedAnswers?.length) {
      const answers = questionData.suggestedAnswers.map(
        (answer) => new FillInTheBlankAnswer(answer, 5, false),
      )
      await this.addFillInTheBlank(answers, questionIndex, metadata)
    }
  }

  private async addSlideBackground(
    questionIndex: number,
    metadata: ElementMetadata,
  ): Promise<void> {
    const backgroundUrl = this.getRandomBackgroundUrl()

    // TODO: migrate to typescript
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    const slideBackground: any = new SlideBackground(
      this.FirebaseService.newId(),
      metadata,
      "", // hex - set to empty string
      backgroundUrl, // url
    )

    await this.AssignmentTrackingService.createElement(
      slideBackground,
      questionIndex,
      false, // isStudentElement
    )
  }

  /**
   * Get a random background URL from the available options
   */
  private getRandomBackgroundUrl(): string {
    const randomIndex = Math.floor(Math.random() * BACKGROUND_URLS.length)
    return BACKGROUND_URLS[randomIndex]
  }

  private async addTextbox(
    text: string,
    questionIndex: number,
    metadata: ElementMetadata,
  ): Promise<void> {
    // TODO: migrate to typescript
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    const textbox: any = new Textbox(
      this.FirebaseService.newId(),
      metadata,
      text,
      new Point(150, 200),
      undefined, // size
      21, // fontSize
      undefined, // padding
      undefined, // colorSpans
      undefined, // activeColor
      undefined, // backgroundColor
      "#25c582", // borderColor
      undefined, // fontFamily
    )

    await this.AssignmentTrackingService.createElement(
      textbox,
      questionIndex,
      false,
    )
  }

  private async addFillInTheBlank(
    answers: FillInTheBlankAnswer[],
    questionIndex: number,
    metadata: ElementMetadata,
  ): Promise<void> {
    // TODO: migrate to typescript
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    const fitb: any = new FillInTheBlankParent(
      this.FirebaseService.newId(),
      metadata,
      new Point(550, 200),
      undefined, // size
      answers,
      21, // fontSize
      FitbAnswerTypes.PLAIN.value,
      "none", // precheck
      null, // questionMetadataId
    )

    await this.AssignmentTrackingService.createElement(
      fitb,
      questionIndex,
      false,
    )
  }

  private createElementMetadata(): ElementMetadata {
    return new ElementMetadata(
      this.user.id,
      UserRoles.TEACHER,
      ElementIntents.CONTENT,
      "1", // version
    )
  }

  private buildPrompt(data: AssignmentFormData): string {
    return `Create an educational assignment with exactly 5-8 questions.

SPECIFICATIONS:
- Topic: ${data.practiceTopic}
- Grade Level: ${data.grade}
- Time: ${data.classTime}
- Curriculum: ${data.curriculum}
- Practice Type: ${data.practiceType}

REQUIREMENTS:
- Age-appropriate for ${data.grade} grade
- Clear, unambiguous language
- Mix of difficulty levels (40% foundational, 40% intermediate, 20% advanced)
- Questions support the learning topic
- Completable within ${data.classTime}

FORMAT (follow exactly):
**Assignment Name:** [Create a concise, engaging name max 80 characters - be creative and descriptive]

**Question 1** [Difficulty: Foundational/Intermediate/Advanced]
[Question text]
**Suggested Answers:** [answer1, answer2, answer3]

**Question 2** [Difficulty: Foundational/Intermediate/Advanced]
[Question text]

Continue for all questions...

CRITICAL FORMATTING RULES: 
- Assignment name must be on the first line
- Suggested Answers MUST be enclosed in square brackets: [answer1, answer2]
- Only include "Suggested Answers:" line for questions with specific, factual answers
- Omit "Suggested Answers:" entirely for open-ended or creative writing prompts
- Each suggested answer must be concise (maximum 20 characters)
- Separate multiple answers with commas inside the brackets
- Assignment name should be professional, clear, and include grade level

EXAMPLES:
**Question 1** [Difficulty: Foundational]
How many legs does a spider have?
**Suggested Answers:** [8, eight]

**Question 2** [Difficulty: Intermediate]
Describe what you learned about photosynthesis.
(no Suggested Answers line for open-ended questions)`
  }

  private async callAICompletionAssistant(prompt: string): Promise<string> {
    const response = await this.HttpService.authPost<{
      content: AiChatResponse
    }>(`${this.environment.serverUrlBase}/v1/assistant/completion`, { prompt })

    return response.content.choices[0]?.message?.content || ""
  }

  private parseResponse(
    aiText: string,
    data: AssignmentFormData,
  ): { questions: ParsedQuestion[]; assignmentName: string } {
    let assignmentName = ""
    let questionsText = aiText

    // Extract assignment name
    const nameMatch = aiText.match(/\*\*Assignment Name:\*\*\s*(.+?)(?:\n|$)/i)
    if (nameMatch) {
      assignmentName = nameMatch[1]
        .trim()
        .replace(/^["']|["']$/g, "") // Remove quotes
        .substring(0, 80) // Ensure max 80 chars for db entry

      // Remove the assignment name line from the text
      questionsText = aiText.replace(
        /\*\*Assignment Name:\*\*\s*.+?(?:\n|$)/i,
        "",
      )
    }

    // Parse questions
    const questions = this.parseQuestions(questionsText)

    // Fallback to generic name if parsing failed
    if (!assignmentName) {
      assignmentName = this.buildAssignmentName(data)
    }

    return { questions, assignmentName }
  }

  private parseQuestions(aiText: string): ParsedQuestion[] {
    const questionRegex =
      /\*\*Question \d+\*\*[^\n]*\n([\s\S]*?)(?=\*\*Question \d+\*\*|$)/g
    const questions: ParsedQuestion[] = []

    let match: RegExpExecArray | null
    while ((match = questionRegex.exec(aiText)) !== null) {
      const parsed = this.parseQuestionBlock(match[1])
      if (parsed) {
        questions.push(parsed)
      }
    }

    // Fallback: use entire text as single question if parsing failed
    return questions.length > 0 ? questions : [{ text: aiText.trim() }]
  }

  private parseQuestionBlock(block: string): ParsedQuestion | null {
    const lines = block.split("\n").filter((line) => line.trim())
    if (lines.length === 0) return null

    let text = ""
    let suggestedAnswers: string[] = []

    for (const line of lines) {
      const trimmed = line.trim()

      // Skip difficulty markers
      if (trimmed.match(/^\[Difficulty:/i)) continue

      // Extract suggested answers
      const answerMatch = trimmed.match(
        /^\*\*Suggested Answers:\*\*\s*\[(.*)\]/i,
      )
      if (answerMatch) {
        suggestedAnswers = answerMatch[1]
          .split(",")
          .map((a) => a.trim().substring(0, 20)) // Max 20 characters for FITB
          .filter((a) => a.length > 0)
        continue
      }

      // Add to question text
      if (trimmed) {
        text += (text ? "\n" : "") + trimmed
      }
    }

    return text
      ? {
          text: text.trim(),
          suggestedAnswers:
            suggestedAnswers.length > 0 ? suggestedAnswers : undefined,
        }
      : null
  }

  private buildAssignmentName(data: AssignmentFormData): string {
    const name =
      `${data.practiceTopic || "Practice"} - ${data.grade || ""}`.trim()
    return this.truncateValue(name, 80)
  }

  private truncateValue(value: string | undefined, maxLength: number): string {
    if (!value) return ""
    return value.length > maxLength ? value.substring(0, maxLength) : value
  }

  closeWizard(): void {
    this.BreadcrumbService.go("root.account.nav.assignments")
  }

  viewAssignment(): void {
    if (this.assignmentId) {
      this.BreadcrumbService.go("root.account.assignment", {
        assignmentId: this.assignmentId,
      })
    }
  }
}
